import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { announcementsApi, type Announcement } from '../api/announcements.api'
import { announcementQueries } from '../api/announcements.queries'
import {
  localDateKey,
  rememberAnnouncementShown,
  wasAnnouncementShown,
} from '../lib/announcement-memory'
import { useAuth } from '@/features/auth/session/auth-context'

/** The closing card's exit animation (.announce-card-out in index.css). */
const LEAVE_MS = 200
/** The pause after a close before the next announcement springs in. */
const NEXT_DELAY_MS = 350

interface Queue {
  /** Unseen announcements, oldest first; the head is the one on screen. */
  items: Announcement[]
  /** How many of this run were closed already: the offset of the "2/3" counter. */
  closed: number
}

/** A card playing its exit animation, with the counter it showed. */
interface LeavingCard {
  announcement: Announcement
  position: number
  total: number
}

/**
 * The day's announcements from the Eleva team, mounted once in the signed-in
 * shell. Asks at app start and whenever the tab comes back into view, then
 * shows every unseen message in its own popup, oldest first, the next one a
 * beat after the previous closes. A popup counts as seen the moment it
 * appears. `blocked` holds the queue while another startup popup (country
 * gate, country status, social sanity intro) is up, so two never stack.
 * Same flow and card as the mobile apps.
 */
export function AnnouncementHost({ blocked }: { blocked: boolean }) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const userId = user?.userId ?? null
  const [queue, setQueue] = useState<Queue>({ items: [], closed: 0 })
  const [leaving, setLeaving] = useState<LeavingCard | null>(null)
  const [resting, setResting] = useState(false)
  const timersRef = useRef<number[]>([])

  useEffect(() => {
    if (!userId) return
    let disposed = false
    const check = () => {
      if (document.visibilityState !== 'visible') return
      queryClient.fetchQuery(announcementQueries.today(localDateKey())).then(
        (list) => {
          if (disposed) return
          const fresh: Announcement[] = []
          for (const announcement of list) {
            if (!wasAnnouncementShown(userId, announcement.id)) fresh.push(announcement)
            // Shown before, but its seen call never landed: send it again.
            else void announcementsApi.markSeen(announcement.id).catch(() => {})
          }
          if (fresh.length === 0) return
          setQueue((current) => {
            const queued = new Set(current.items.map((item) => item.id))
            const added = fresh.filter((item) => !queued.has(item.id))
            return added.length > 0 ? { ...current, items: [...current.items, ...added] } : current
          })
        },
        () => {
          // Offline or server trouble: the next return to the tab asks again.
        },
      )
    }
    check()
    document.addEventListener('visibilitychange', check)
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', check)
    }
  }, [queryClient, userId])

  useEffect(() => {
    const timers = timersRef.current
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [])

  const head = queue.items[0]
  const current = head && !blocked && !resting && !leaving ? head : null
  const currentId = current?.id ?? null

  // Seen the moment it shows: remembered locally first, so a failed call can
  // never bring it back, then reported (the next check retries a failure).
  useEffect(() => {
    if (!currentId || !userId) return
    rememberAnnouncementShown(userId, currentId)
    void announcementsApi.markSeen(currentId).catch(() => {})
  }, [currentId, userId])

  const position = queue.closed + 1
  const total = queue.closed + queue.items.length

  const dismiss = () => {
    if (!current) return
    setLeaving({ announcement: current, position, total })
    setQueue((state) => {
      const items = state.items.slice(1)
      return { items, closed: items.length > 0 ? state.closed + 1 : 0 }
    })
    setResting(true)
    timersRef.current.push(
      window.setTimeout(() => setLeaving(null), LEAVE_MS),
      window.setTimeout(() => setResting(false), NEXT_DELAY_MS),
    )
  }

  const nextWaiting = queue.items.length > 0 && !blocked
  if (!current && !leaving && !(resting && nextWaiting)) return null

  return createPortal(
    // Only the button closes it (Escape works like the button, as Android's
    // back does): a click on the backdrop does nothing. Between two cards
    // the backdrop stays, after the last one it fades with the card.
    <div
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        event.stopPropagation()
        dismiss()
      }}
      className={`announce-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm outline-none ${
        current || nextWaiting ? 'announce-backdrop-in' : 'announce-backdrop-out'
      }`}
    >
      {current ? (
        <AnnouncementCard
          key={current.id}
          message={current.message}
          position={position}
          total={total}
          onDismiss={dismiss}
        />
      ) : leaving ? (
        <AnnouncementCard
          key={leaving.announcement.id}
          message={leaving.announcement.message}
          position={leaving.position}
          total={leaving.total}
          leaving
        />
      ) : null}
    </div>,
    document.body,
  )
}

/**
 * One announcement: a gradient band with the megaphone popping in over
 * twinkling confetti, the playful title, the admin's message (line breaks
 * kept, scrolling when long) and a single button. A "1/2" counter sits in the
 * band's corner while more than one is lined up.
 */
function AnnouncementCard({
  message,
  position,
  total,
  leaving = false,
  onDismiss,
}: {
  message: string
  position: number
  total: number
  /** Playing the exit animation: frozen, the next card follows. */
  leaving?: boolean
  onDismiss?: () => void
}) {
  const { t } = useTranslation('common')
  const titleId = useId()
  // Focus lands on the dialog itself (screen readers announce it, Escape
  // reaches the overlay) rather than on the button, which would wear a focus
  // ring after a mouse click on the previous card.
  const dialogRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  // A long message scrolls: its text fades out on the edge it continues past.
  const messageRef = useRef<HTMLParagraphElement>(null)
  const [more, setMore] = useState({ above: false, below: false })
  useEffect(() => {
    const node = messageRef.current
    if (!node) return
    const measure = () => {
      const above = node.scrollTop > 1
      const below = node.scrollTop + node.clientHeight < node.scrollHeight - 1
      setMore((current) =>
        current.above === above && current.below === below ? current : { above, below },
      )
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    node.addEventListener('scroll', measure, { passive: true })
    return () => {
      observer.disconnect()
      node.removeEventListener('scroll', measure)
    }
  }, [])
  const fade =
    more.above || more.below
      ? `linear-gradient(to bottom, ${more.above ? 'transparent' : '#000'}, #000 28px, #000 calc(100% - 28px), ${more.below ? 'transparent' : '#000'})`
      : undefined

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={`bg-canvas border-border flex max-h-full w-full max-w-[360px] flex-col overflow-hidden rounded-[28px] border shadow-2xl outline-none ${
        leaving ? 'announce-card-out pointer-events-none' : 'announce-card-in'
      }`}
    >
      <div className="from-primary to-secondary relative flex h-32 shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br">
        <Confetti />
        {total > 1 && (
          <span className="absolute top-3.5 right-4 rounded-full bg-black/25 px-2.5 py-0.5 text-xs font-bold text-white tabular-nums">
            {position}/{total}
          </span>
        )}
        <span className="announce-pop relative flex size-20 items-center justify-center rounded-full bg-white shadow-xl ring-4 ring-white/30">
          <span className="announce-wiggle text-[42px] leading-none" aria-hidden>
            📣
          </span>
        </span>
      </div>

      <div className="flex min-h-0 flex-col items-center px-6 pt-5 pb-6 text-center">
        <h2
          id={titleId}
          className="text-primary dark:text-secondary text-xl font-black tracking-tight"
        >
          {t('announcement.title')}
        </h2>
        <p
          ref={messageRef}
          style={fade ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
          className="text-fg mt-3 max-h-[40dvh] min-h-0 overflow-y-auto text-[15px] leading-relaxed break-words whitespace-pre-wrap"
        >
          {message}
        </p>
        {/* The gap above the button: while the message goes on below, a little nudge to scroll. */}
        <span aria-hidden className="flex h-7 shrink-0 items-center justify-center">
          {more.below && (
            <ChevronDown className="text-fg-subtle size-4 animate-bounce motion-reduce:animate-none" />
          )}
        </span>
        <button
          type="button"
          disabled={leaving}
          onClick={onDismiss}
          className="bg-primary hover:bg-primary-hover w-full shrink-0 rounded-2xl py-3 text-[15px] font-bold text-white shadow-lg transition active:scale-[0.98]"
        >
          {t('announcement.dismiss')}
        </button>
      </div>
    </div>
  )
}

/** Twinkling sparkles and a few drifting confetti bits around the megaphone. */
function Confetti() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      <Sparkle className="top-4 left-7 size-4" delay={0} />
      <Sparkle className="top-[60%] left-[16%] size-3" delay={0.8} />
      <Sparkle className="top-5 right-[25%] size-3.5" delay={1.4} />
      <Sparkle className="right-7 bottom-5 size-5" delay={0.4} />
      <Bit className="top-[24%] left-[30%] h-3 w-1.5 rounded-sm bg-white/70" delay={0} />
      <Bit className="right-[29%] bottom-[16%] size-2 rounded-full bg-white/60" delay={1.1} />
      <Bit
        className="bottom-3 left-[34%] size-2.5 rounded-full border-2 border-white/70"
        delay={0.6}
      />
      <Bit className="top-[42%] right-[13%] h-1.5 w-3 rounded-sm bg-white/50" delay={1.7} />
    </span>
  )
}

/** A four-point star; `delay` (seconds) staggers the twinkle. */
function Sparkle({ className, delay }: { className: string; delay: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`announce-twinkle absolute fill-white ${className}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <path d="M12 0C12.8 6.6 17.4 11.2 24 12 17.4 12.8 12.8 17.4 12 24 11.2 17.4 6.6 12.8 0 12 6.6 11.2 11.2 6.6 12 0Z" />
    </svg>
  )
}

/** A confetti piece that bobs and turns; `delay` (seconds) staggers it. */
function Bit({ className, delay }: { className: string; delay: number }) {
  return (
    <span
      className={`announce-drift absolute ${className}`}
      style={{ animationDelay: `${delay}s` }}
    />
  )
}
